import {
  BellAlertIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  ListBulletIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { AccessRequest } from "types/AccessRequest";
import AlertsContext from "common/providers/alerts";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import AccessRequestItem from "./AccessRequestItem";

function AccessRequests() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<AccessRequest>("access-requests", {
    defaultFilters: {},
    defaultSort: "createdAt:ASC",
    cachePrefix: "AccessRequests_all",
    populate: ["user", "contact"],
  });

  const { items: dataItems } = useList<AccessRequest>("access-requests", {
    defaultFilters: {},
    defaultSort: "createdAt:ASC",
    populate: ["user"],
    defaultItemsPerPage: 5000,
  });

  const { updateItem } = useItem<AccessRequest>("access-requests", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleRefuse = useCallback(
    async (id: any) => {
      try {
        await updateItem(id, { state: "refused" });
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [updateItem, fetchItems, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/access-requests/edit/${id}`);
  };

  const statusList = useMemo(() => {
    return [
      {
        id: "",
        name: t("status.all"),
        count: dataItems?.length || 0,
        icon: ListBulletIcon,
        color: "secondary",
      },
      {
        id: "pending",
        name: t("common.stateWaitingForApproval"),
        count:
          dataItems?.filter((item: any) => item.state === "pending")?.length ||
          0,
        icon: ClipboardDocumentListIcon,
        color: "yellow",
      },
      {
        id: "accepted",
        name: t("common.stateApproved"),
        count:
          dataItems?.filter((item: any) => item.state === "accepted")?.length ||
          0,
        icon: CheckIcon,
        color: "green",
      },
      {
        id: "refused",
        name: t("common.stateRefused"),
        count:
          dataItems?.filter((item: any) => item.state === "refused")?.length ||
          0,
        icon: XMarkIcon,
        color: "red",
      },
    ];
  }, [dataItems, t]);

  const setOverallFilter = useCallback(
    (key: string, value: string | number | boolean) => {
      setFilter(key, value);
    },
    [setFilter]
  );

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "user][username$contains",
        filterLabel: t("filters.search"),
        columnClasses: "lg:flex-1",
      },
      {
        Header: t("forms.email"),
        filterName: "user][email$contains",
        filterLabel: t("filters.search"),
        columnClasses: "lg:flex-1",
      },
      {
        Header: t("forms.quality"),
        filterName: "quality$contains",
        filterLabel: t("filters.search"),
        columnClasses: "lg:flex-1",
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.accessRequests")}
        subtitle={t("titleLegends.accessRequests")}
        Icon={BellAlertIcon}
        isBeforeFilters
        filters={filters}
        setFilter={setOverallFilter}
        filterName="state$contains"
        FilterComponent={(props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        )}
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={AccessRequestItem}
        onDelete={handleRefuse}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default AccessRequests;
